/**
 * KPI Card Component
 *
 * Displays a single KPI metric in a Material-UI Card.
 * Features:
 * - Formatted value display
 * - Optional change indicator with color coding
 * - Responsive design
 */

import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import type { KPIMetric } from '../../types/financialInsights';

interface KPICardProps {
  /** KPI metric data */
  metric: KPIMetric;
}

/**
 * KPI Card component for displaying financial metrics
 */
export const KPICard: React.FC<KPICardProps> = ({ metric }) => {
  /**
   * Get icon and color for change indicator based on direction
   */
  const getChangeIndicator = () => {
    if (!metric.change || !metric.change_direction) {
      return null;
    }

    const isPositive = metric.change_direction === 'up';
    const isNegative = metric.change_direction === 'down';
    const isNeutral = metric.change_direction === 'neutral';

    let icon: React.ReactNode = null;
    let color: string = 'text.secondary';

    if (isPositive) {
      icon = <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />;
      color = 'success.main';
    } else if (isNegative) {
      icon = <TrendingDown sx={{ fontSize: 16, mr: 0.5 }} />;
      color = 'error.main';
    } else if (isNeutral) {
      icon = <RemoveIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      color = 'text.secondary';
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color,
          mt: 0.5,
        }}
      >
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {Math.abs(metric.change).toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
        }}
      >
        {/* KPI Label */}
        <Typography
          variant="subtitle2"
          color="text.secondary"
          gutterBottom
          sx={{
            fontWeight: 500,
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {metric.label}
        </Typography>

        {/* KPI Value */}
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: 28, sm: 32, md: 34 },
            mt: 1,
            mb: 0.5,
            lineHeight: 1.2,
          }}
        >
          {metric.formatted_value}
        </Typography>

        {/* Change Indicator */}
        {getChangeIndicator()}
      </CardContent>
    </Card>
  );
};

export default KPICard;
